import { Logger } from '../../utils/logger';
import {
  DataFetchError,
  DataSource,
  FetchErrorType,
  classifyFetchError,
  isRetryableErrorType,
  createDataFetchError,
  extractStatusCode,
  DataFetchErrorContext,
} from './types';

// Re-export types for convenience
export type {
  DataFetchError,
  DataSource,
  FetchErrorType,
  DataFetchErrorContext,
};
export {
  classifyFetchError,
  isRetryableErrorType,
  createDataFetchError,
  extractStatusCode,
};

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export type ErrorCategory =
  | 'audio'
  | 'network'
  | 'player'
  | 'validation'
  | 'permission'
  | 'data'
  | 'unknown';

export interface AppError {
  id: string;
  message: string;
  originalError?: Error;
  severity: ErrorSeverity;
  category: ErrorCategory;
  timestamp: Date;
  context?: Record<string, unknown>;
  recoverable: boolean;
  userMessage?: string;
}

export interface ErrorReport {
  error: AppError;
  stackTrace?: string;
  userAgent: string;
  url: string;
  userId?: string;
}

export class ErrorHandlerService {
  private static instance: ErrorHandlerService;
  private errorHistory: AppError[] = [];
  private maxHistorySize = 100;
  private errorCallbacks = new Set<(error: AppError) => void>();

  private constructor() { }

  static getInstance(): ErrorHandlerService {
    if (!ErrorHandlerService.instance) {
      ErrorHandlerService.instance = new ErrorHandlerService();
    }
    return ErrorHandlerService.instance;
  }

  /**
   * Handles an error and creates a standardized AppError
   */
  handleError(
    error: Error | string,
    category: ErrorCategory = 'unknown',
    context?: Record<string, unknown>,
    severity: ErrorSeverity = 'medium'
  ): AppError {
    const appError: AppError = {
      id: this.generateErrorId(),
      message: typeof error === 'string' ? error : error.message,
      originalError: typeof error === 'string' ? new Error(error) : error,
      severity,
      category,
      timestamp: new Date(),
      context,
      recoverable: this.isRecoverableError(error, category),
      userMessage: this.generateUserMessage(error, category)
    };

    this.processError(appError);
    return appError;
  }

  /**
   * Handles audio-specific errors
   */
  handleAudioError(error: Error, context?: Record<string, unknown>): AppError {
    return this.handleError(error, 'audio', context, this.getAudioErrorSeverity(error));
  }

  /**
   * Handles network errors
   */
  handleNetworkError(error: Error, context?: Record<string, unknown>): AppError {
    return this.handleError(error, 'network', context, this.getNetworkErrorSeverity(error));
  }

  /**
   * Handles player errors
   */
  handlePlayerError(error: Error, context?: Record<string, unknown>): AppError {
    return this.handleError(error, 'player', context, this.getPlayerErrorSeverity(error));
  }

  /**
   * Handles data fetch errors (R2, ATC CDN, etc.)
   *
   * This is the primary method for routing data fetching errors through the
   * centralized error pipeline. It accepts a DataFetchError which includes
   * classification, retryability, and source information.
   *
   * @param dataError - Normalized DataFetchError from types.ts
   * @returns Processed AppError
   */
  handleDataError(dataError: DataFetchError): AppError {
    const severity = this.getDataErrorSeverity(dataError);

    const context: Record<string, unknown> = {
      source: dataError.source,
      errorType: dataError.errorType,
      retryable: dataError.retryable,
      ...(dataError.statusCode !== undefined && { statusCode: dataError.statusCode }),
      ...dataError.context,
    };

    return this.handleError(
      dataError.originalError,
      'data',
      context,
      severity
    );
  }

  /**
   * Convenience method to create and handle a data error in one call
   *
   * @param error - The caught error
   * @param source - Data source that failed
   * @param context - Optional context data
   * @returns Processed AppError
   */
  handleDataFetchError(
    error: unknown,
    source: DataSource,
    context?: DataFetchErrorContext
  ): AppError {
    const statusCode = extractStatusCode(error);
    const dataError = createDataFetchError(error, source, context, statusCode);
    return this.handleDataError(dataError);
  }

  /**
   * Processes an error (logging, storing, notifying)
   */
  private processError(error: AppError): void {
    // Add to history
    this.addToHistory(error);

    // Log the error
    this.logError(error);

    // Notify listeners
    this.notifyErrorListeners(error);

    // Check if error should be reported
    if (error.severity === 'high' || error.severity === 'critical') {
      this.reportError(error);
    }
  }

  /**
   * Adds error to history
   */
  private addToHistory(error: AppError): void {
    this.errorHistory.unshift(error);

    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory = this.errorHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Logs error with appropriate level
   */
  private logError(error: AppError): void {
    const logContext = {
      errorId: error.id,
      category: error.category,
      severity: error.severity,
      recoverable: error.recoverable,
      ...error.context
    };

    switch (error.severity) {
      case 'low':
        Logger.debug(`[${error.category.toUpperCase()}] ${error.message}`, 'error-handler', logContext);
        break;
      case 'medium':
        Logger.warn(`[${error.category.toUpperCase()}] ${error.message}`, 'error-handler', logContext);
        break;
      case 'high':
        Logger.error(`[${error.category.toUpperCase()}] ${error.message}`, 'error-handler', error.originalError, logContext);
        break;
      case 'critical':
        Logger.exception(error.originalError || error, `[${error.category.toUpperCase()}] ${error.message}`, 'error-handler');
        break;
    }
  }

  /**
   * Notifies all error listeners
   */
  private notifyErrorListeners(error: AppError): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (callbackError) {
        Logger.error('Error in error callback:', 'error-handler', callbackError);
      }
    });
  }

  /**
   * Reports error to external service (placeholder)
   */
  private reportError(error: AppError): void {
    const errorReport: ErrorReport = {
      error,
      stackTrace: error.originalError?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId()
    };

    // In a real implementation, you would send this to an error reporting service
    Logger.warn('Error report generated:', 'error-handler', errorReport);
  }

  /**
   * Determines if an error is recoverable
   */
  private isRecoverableError(error: Error | string, category: ErrorCategory): boolean {
    const errorMessage = typeof error === 'string' ? error : error.message;

    switch (category) {
      case 'audio':
        return errorMessage.includes('NotAllowedError') ||
          errorMessage.includes('NotAllowedError') ||
          errorMessage.includes('Autoplay failed');

      case 'network':
        return errorMessage.includes('timeout') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('fetch failed');

      case 'player':
        return errorMessage.includes('restricted') ||
          errorMessage.includes('unavailable') ||
          errorMessage.includes('Not Found');

      case 'validation':
        return true; // Validation errors are typically recoverable

      case 'data':
        // Data errors are recoverable if they're transient (network/timeout/server)
        return errorMessage.includes('timeout') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('fetch failed') ||
          errorMessage.includes('500') ||
          errorMessage.includes('503');

      default:
        return false;
    }
  }

  /**
   * Generates user-friendly error message
   */
  private generateUserMessage(error: Error | string, category: ErrorCategory): string {
    const errorMessage = typeof error === 'string' ? error : error.message;

    switch (category) {
      case 'audio':
        if (errorMessage.includes('NotAllowedError') || errorMessage.includes('Autoplay')) {
          return 'Please interact with the page to enable audio playback.';
        }
        return 'Audio playback error. Please try again.';

      case 'network':
        if (errorMessage.includes('timeout')) {
          return 'Connection timed out. Please check your internet connection.';
        }
        return 'Network error. Please check your connection and try again.';

      case 'player':
        if (errorMessage.includes('restricted')) {
          return 'This content is restricted in your region.';
        }
        return 'Player error. The next track will be played automatically.';

      case 'validation':
        return 'Invalid input. Please check your entry and try again.';

      case 'data':
        if (errorMessage.includes('timeout')) {
          return 'Data loading timed out. Please try again.';
        }
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          return 'Requested data was not found.';
        }
        if (errorMessage.includes('offline') || errorMessage.includes('network')) {
          return 'Unable to load data. Please check your connection.';
        }
        return 'Failed to load data. Using cached version.';

      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Determines audio error severity
   */
  private getAudioErrorSeverity(error: Error): ErrorSeverity {
    if (error.message.includes('NotAllowedError')) {
      return 'low'; // Autoplay issues are low severity
    }
    if (error.message.includes('not found') || error.message.includes('404')) {
      return 'medium';
    }
    return 'medium';
  }

  /**
   * Determines network error severity
   */
  private getNetworkErrorSeverity(error: Error): ErrorSeverity {
    if (error.message.includes('timeout')) {
      return 'medium';
    }
    if (error.message.includes('fetch failed') || error.message.includes('NetworkError')) {
      return 'high';
    }
    return 'medium';
  }

  /**
   * Determines player error severity
   */
  private getPlayerErrorSeverity(error: Error): ErrorSeverity {
    if (error.message.includes('restricted')) {
      return 'low'; // Content restrictions are expected
    }
    if (error.message.includes('unavailable')) {
      return 'medium';
    }
    return 'medium';
  }

  /**
   * Determines data fetch error severity based on error type and source
   */
  private getDataErrorSeverity(dataError: DataFetchError): ErrorSeverity {
    const { errorType, source } = dataError;

    // 404 errors are expected for stale scenarios
    if (errorType === 'not-found') {
      return 'low';
    }

    // Parse errors indicate data corruption or API changes
    if (errorType === 'parse-error') {
      return 'high';
    }

    // Network/timeout errors are transient
    if (errorType === 'network' || errorType === 'timeout') {
      return 'medium';
    }

    // Server errors are concerning but recoverable
    if (errorType === 'server-error') {
      return 'medium';
    }

    // Client errors (except 404) indicate bugs
    if (errorType === 'client-error') {
      return 'high';
    }

    // Source-based severity adjustment
    // Critical services get higher severity
    if (source === 'r2' || source === 'atc-cdn') {
      return 'medium';
    }

    return 'medium';
  }

  /**
   * Generates unique error ID
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Gets current user ID (placeholder)
   */
  private getCurrentUserId(): string | undefined {
    // In a real implementation, you would get this from auth service
    return undefined;
  }

  /**
   * Adds error event listener
   */
  addErrorListener(callback: (error: AppError) => void): void {
    this.errorCallbacks.add(callback);
  }

  /**
   * Removes error event listener
   */
  removeErrorListener(callback: (error: AppError) => void): void {
    this.errorCallbacks.delete(callback);
  }

  /**
   * Gets error history
   */
  getErrorHistory(): AppError[] {
    return [...this.errorHistory];
  }

  /**
   * Gets recent errors by category
   */
  getRecentErrors(category: ErrorCategory, minutes: number = 5): AppError[] {
    const cutoffTime = new Date(Date.now() - minutes * 60 * 1000);

    return this.errorHistory.filter(error =>
      error.category === category && error.timestamp >= cutoffTime
    );
  }

  /**
   * Clears error history
   */
  clearErrorHistory(): void {
    this.errorHistory = [];
    Logger.info('Error history cleared', 'error-handler');
  }

  /**
   * Gets error statistics
   */
  getErrorStats(): {
    total: number;
    byCategory: Record<ErrorCategory, number>;
    bySeverity: Record<ErrorSeverity, number>;
    recent: number; // Last hour
  } {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    const byCategory: Record<ErrorCategory, number> = {
      audio: 0,
      network: 0,
      player: 0,
      validation: 0,
      permission: 0,
      data: 0,
      unknown: 0
    };

    const bySeverity: Record<ErrorSeverity, number> = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    };

    let recent = 0;

    this.errorHistory.forEach(error => {
      byCategory[error.category]++;
      bySeverity[error.severity]++;

      if (error.timestamp >= oneHourAgo) {
        recent++;
      }
    });

    return {
      total: this.errorHistory.length,
      byCategory,
      bySeverity,
      recent
    };
  }
}